import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  DEFAULT_SHRINKAGE,
  JUMP_LETTERS,
  type SortDirection,
  type SortSpec,
  albumScore,
  applySort,
  filedSorts,
  railTargets,
} from "@trackmark/core";
import { RankTable, rankTableStyles as table } from "../design/RankTable.js";
import { RatingStrip } from "../design/RatingStrip.js";
import { SortHead, SortRow } from "../design/Sort.js";
import { ARTIST_SORTS, ArtistTable, type ArtistRow, orderedArtists } from "./ArtistTable.js";
import styles from "./Library.module.css";

/**
 * The library — what you HAVE, as opposed to rankings, which is what is best.
 *
 * Five tabs, but only three tables: `NEED TO RATE` and `INCOMPLETE` are the songs and albums
 * tables with a preset filter, a different default sort, and their own empty states
 * (docs/DESIGN-CONFLICTS.md §J14). Building them as separate screens would be the same table
 * three times, drifting apart.
 *
 * Row height is the token, not the 26px design/README.md §6 asks for: §J12 ruled one row height
 * across every table in the app, and three heights across three tables is a defect repeated
 * three times rather than nuance.
 *
 * **The order is the client's now.** It used to be the server's, with a rail letter attached to
 * each row — which worked exactly as long as there was one order. A column heading that sorts
 * means six orders and two directions, and the whole list is already here and virtualised, so
 * asking the server to re-file two thousand rows is a round trip to learn something this screen
 * can work out between two frames. `packages/core/src/filing.ts` holds the rules, so the order
 * and the rail come from one definition rather than two that agree until they don't.
 */

export interface LibrarySong {
  id: number;
  title: string;
  artist: string;
  artistId: number;
  album: string;
  albumId: number;
  year: number | null;
  score: number | null;
  addedAt: string | null;
  letter: string;
  art: string | null;
}

export interface LibraryAlbum {
  id: number;
  title: string;
  artist: string;
  year: number | null;
  primaryType: string;
  isTracked: boolean;
  coreCount: number;
  ratedCore: number;
  cohesion: number | null;
  addedAt: string | null;
  /** The ingredients for `albumScore()`, so the tile and the album page agree on the number. */
  tracks: { rating: number | null; isCore: boolean; durationMs: number | null }[];
  letter: string;
  art: string | null;
}

export type LibraryTab = "songs" | "albums" | "artists" | "need-to-rate" | "incomplete";

export const LIBRARY_TABS: { tab: LibraryTab; label: string }[] = [
  { tab: "songs", label: "SONGS" },
  { tab: "albums", label: "ALBUMS" },
  { tab: "artists", label: "ARTISTS" },
  { tab: "need-to-rate", label: "NEED TO RATE" },
  { tab: "incomplete", label: "INCOMPLETE" },
];

const TITLES: Record<LibraryTab, string> = {
  songs: "Songs",
  albums: "Albums",
  artists: "Artists",
  "need-to-rate": "Need to rate",
  incomplete: "Incomplete albums",
};

/**
 * Distinct empty states, because the two variants mean opposite things when empty (§J14).
 * "No songs" on a fresh library is a different sentence from "nothing left to rate".
 */
const EMPTY: Record<LibraryTab, { title: string; hint: string }> = {
  songs: { title: "No songs yet", hint: "IMPORT A LIBRARY TO FILL THIS" },
  albums: { title: "No albums yet", hint: "IMPORT A LIBRARY TO FILL THIS" },
  artists: { title: "No artists yet", hint: "IMPORT A LIBRARY TO FILL THIS" },
  "need-to-rate": { title: "Everything is rated", hint: "NOTHING IS WAITING FOR YOU" },
  incomplete: { title: "Every record is complete", hint: "NO ALBUM IS SHORT OF A RATING" },
};

/*
 * 40px of artwork column for a 24px thumbnail, exactly as the rankings table has always had it
 * (design/README.md §3: `ARTWORK (40)`). This was 24 — the width of the image itself — so the
 * cover sat flush against the title with no gutter at all and the two read as one smudged column.
 */
const SONG_COLUMNS = "40px 1fr 190px 210px 116px 40px 50px 76px";

/** Each tab opens on the sort that answers the question the tab is asking (§J14). */
export const DEFAULT_SORT: Record<LibraryTab, { sort: string; direction: SortDirection }> = {
  songs: { sort: "title", direction: "asc" },
  albums: { sort: "title", direction: "asc" },
  artists: { sort: "rank", direction: "asc" },
  // Oldest first: the backlog is a queue, and the thing waiting longest is the thing to do next.
  "need-to-rate": { sort: "added", direction: "asc" },
  incomplete: { sort: "title", direction: "asc" },
};

const dateLabel = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const M = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return `${String(d.getUTCDate()).padStart(2, "0")} ${M[d.getUTCMonth()]} ${String(d.getUTCFullYear() % 100).padStart(2, "0")}`;
};

export interface LibraryProps {
  tab: LibraryTab;
  onTab: (tab: LibraryTab) => void;
  songs: LibrarySong[];
  albums: LibraryAlbum[];
  artists: ArtistRow[];
  collection: { totalRated: number; mean: number | null };
  settings?: typeof DEFAULT_SHRINKAGE;
  /** In the URL, so a sorted view is a link — the same reason the tab is a path segment. */
  sort: string;
  direction: SortDirection;
  onSort: (sort: string, direction: SortDirection) => void;
}

/** An album's own number, or null while it is short of a rating — the tile's `INCOMPLETE`. */
function scoreOf(album: LibraryAlbum): number | null {
  if (!album.isTracked) return null;
  const result = albumScore(album.tracks, album.cohesion);
  return result.complete ? result.score : null;
}

export function Library({
  tab,
  onTab,
  songs,
  albums,
  artists,
  collection,
  settings = DEFAULT_SHRINKAGE,
  sort,
  direction,
  onSort,
}: LibraryProps) {
  const [jumpTo, setJumpTo] = useState<{ index: number; token: number } | null>(null);
  const [active, setActive] = useState<string | null>(null);

  const songTab = tab === "songs" || tab === "need-to-rate";
  const albumTab = tab === "albums" || tab === "incomplete";

  const songSorts = useMemo(
    () =>
      filedSorts<LibrarySong>({
        title: (s) => s.title,
        artist: (s) => s.artist,
        album: (s) => s.album,
        year: (s) => s.year,
        score: (s) => s.score,
        addedAt: (s) => s.addedAt,
      }),
    [],
  );

  const albumSorts = useMemo(
    () =>
      filedSorts<LibraryAlbum>({
        title: (a) => a.title,
        artist: (a) => a.artist,
        year: (a) => a.year,
        // The same function the tile prints and the album page shows, so ordering by score cannot
        // disagree with the number beside it.
        score: scoreOf,
        addedAt: (a) => a.addedAt,
      }),
    [],
  );

  /*
   * A sort that does not exist on this tab falls back to the tab's own rather than emptying the
   * screen: ALBUM is a column on Songs and not on Albums, RANK exists only on Artists, and
   * switching tabs must not strand you on a column that is not there.
   */
  const sorts = tab === "artists" ? ARTIST_SORTS : songTab ? songSorts : albumSorts;
  const field = sort in sorts ? sort : DEFAULT_SORT[tab].sort;
  const spec = sorts[field]!;

  const sortedSongs = useMemo(
    () => (songTab ? applySort(songs, spec as SortSpec<LibrarySong>, direction) : songs),
    [songTab, songs, spec, direction],
  );
  const sortedAlbums = useMemo(
    () => (albumTab ? applySort(albums, spec as SortSpec<LibraryAlbum>, direction) : albums),
    [albumTab, albums, spec, direction],
  );
  const sortedArtists = useMemo(
    () => (tab === "artists" ? orderedArtists(artists, collection, settings, field, direction) : []),
    [tab, artists, collection, settings, field, direction],
  );

  const rows: unknown[] = tab === "artists" ? sortedArtists : songTab ? sortedSongs : sortedAlbums;
  const rail = useMemo(() => railTargets(rows, spec as SortSpec<unknown>, JUMP_LETTERS), [rows, spec]);

  const jump = (target: { label: string; index: number | null }) => {
    if (target.index === null) return;
    setActive(target.label);
    setJumpTo((previous) => ({ index: target.index!, token: (previous?.token ?? 0) + 1 }));
  };

  /** Clicking the column you are on turns it around; clicking another starts it its own way up. */
  const head = (column: string, label: string, initial: SortDirection = "asc", align: "left" | "right" = "left") => (
    <SortHead
      label={label}
      align={align}
      active={field === column}
      direction={direction}
      onSort={() => onSort(column, field === column ? (direction === "asc" ? "desc" : "asc") : initial)}
    />
  );

  const empty = EMPTY[tab];
  const count =
    tab === "artists"
      ? `${artists.length.toLocaleString()} ARTISTS`
      : songTab
        ? `${songs.length.toLocaleString()} SONGS`
        : `${albums.length.toLocaleString()} ALBUMS`;

  return (
    <div className={styles.page}>
      <div className={styles.title}>{TITLES[tab]}</div>

      <div className={styles.tabs}>
        {LIBRARY_TABS.map((t) => (
          <button
            key={t.tab}
            type="button"
            className={styles.tab}
            data-active={t.tab === tab}
            onClick={() => onTab(t.tab)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/*
        The line under the tabs carries the count, and on the grids the sort.
        `RECORDS I OWN` stood here — a filter on `is_tracked`, which is a word from the schema
        describing whether a record's full track list has been pulled. Nobody owns a tracked
        album, and a toggle nobody can read is a toggle nobody should be offered.
      */}
      {/* On ARTISTS this line would be empty: the table prints its own count, and the headings
          are its sort. An empty 14px band is not chrome. */}
      {tab !== "artists" && (
      <div className={styles.filters}>
        {albumTab && (
          <SortRow
            options={[
              { field: "title", label: "TITLE" },
              { field: "artist", label: "ARTIST" },
              { field: "score", label: "SCORE", initial: "desc" },
              { field: "year", label: "YEAR", initial: "desc" },
              { field: "added", label: "ADDED", initial: "desc" },
            ]}
            active={field}
            direction={direction}
            onSort={onSort}
          />
        )}
        <span className={styles.count} data-testid="library-count">
          {count}
        </span>
      </div>
      )}

      {rows.length === 0 ? (
        <div className={styles.empty} data-testid="library-empty">
          <div className={styles.emptyTitle}>{empty.title}</div>
          <div className={styles.emptyHint}>{empty.hint}</div>
        </div>
      ) : (
        <div className={styles.body}>
          <div className={styles.main}>
            {tab === "artists" ? (
              <ArtistTable
                rows={sortedArtists}
                collection={collection}
                settings={settings}
                sort={field}
                direction={direction}
                onSort={onSort}
                jumpTo={jumpTo}
              />
            ) : songTab ? (
              <RankTable
                rows={sortedSongs}
                columns={SONG_COLUMNS}
                rowKey={(s) => s.id}
                jumpTo={jumpTo}
                rowProps={(s) => ({ "data-state": s.score === null ? "unrated" : undefined })}
                header={
                  <>
                    <div />
                    {head("title", "TITLE")}
                    {head("artist", "ARTIST")}
                    {head("album", "ALBUM")}
                    <div>RATING</div>
                    {head("score", "SCR", "desc", "right")}
                    {head("year", "YEAR", "desc", "right")}
                    {head("added", "ADDED", "desc", "right")}
                  </>
                }
                renderRow={(s) => (
                  <>
                    {/* Four destinations on one row, each on the thing it names: the cover and the
                        album go to the record, the title goes to the record with this track under
                        the cursor, and the artist goes to the artist. */}
                    <Link to="/album/$albumId" params={{ albumId: String(s.albumId) }} aria-label={`Go to ${s.album}`}>
                      {s.art ? (
                        <img className={table.art} src={s.art} alt="" />
                      ) : (
                        <div className={table.artFallback}>{s.album.slice(0, 8)}</div>
                      )}
                    </Link>
                    <Link
                      to="/album/$albumId"
                      params={{ albumId: String(s.albumId) }}
                      search={{ song: s.id }}
                      className={table.titleLink}
                      data-testid="song-link"
                    >
                      {s.title}
                    </Link>
                    <Link
                      to="/artist/$artistId"
                      params={{ artistId: String(s.artistId) }}
                      className={table.secondaryLink}
                      data-testid="song-artist-link"
                    >
                      {s.artist}
                    </Link>
                    <Link
                      to="/album/$albumId"
                      params={{ albumId: String(s.albumId) }}
                      className={table.secondaryLink}
                      data-testid="song-album-link"
                    >
                      {s.album}
                    </Link>
                    <RatingStrip value={s.score} />
                    {s.score === null ? <div className={table.unr}>UNR</div> : <div className={table.score}>{s.score}</div>}
                    <div className={table.meta}>{s.year ?? "—"}</div>
                    <div className={table.meta}>{dateLabel(s.addedAt)}</div>
                  </>
                )}
              />
            ) : (
              <AlbumGrid albums={sortedAlbums} jumpTo={jumpTo} />
            )}
          </div>

          <nav className={styles.rail} aria-label="Jump" data-testid="jump-rail">
            {rail.map((target) => (
              <button
                key={target.label}
                type="button"
                className={styles.railLetter}
                data-active={active === target.label || undefined}
                data-empty={target.index === null ? "true" : undefined}
                disabled={target.index === null}
                onClick={() => jump(target)}
              >
                {target.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </div>
  );
}

/**
 * The covers grid.
 *
 * An incomplete record reads `INCOMPLETE` and carries a `flash` band; it never shows a number,
 * because a partial score is the defect both source documents name (§J2, and the album page makes
 * the same refusal).
 *
 * The columns were `repeat(6, 1fr)`, and `1fr` is `minmax(auto, 1fr)` — so a column could not be
 * narrower than its widest tile's MIN-CONTENT, and a tile contains a title set `nowrap`.
 * `(500) Days of Summer (Music from the Motion Picture)` therefore made its column as wide as that
 * sentence, and the grid grew until the page scrolled sideways. The fix is the explicit minimum:
 * a track's title can no longer vote on how wide a column is.
 */
function AlbumGrid({ albums, jumpTo }: { albums: LibraryAlbum[]; jumpTo: { index: number; token: number } | null }) {
  const tiles = useMemo(() => albums.map((a) => ({ album: a, score: scoreOf(a) })), [albums]);
  const grid = useRef<HTMLDivElement>(null);

  /*
   * The grid's answer to the table's virtualiser. Tiles are the container's own children, so the
   * rail's index is a child index — no second list, and nothing to keep in step.
   */
  useEffect(() => {
    if (jumpTo === null) return;
    grid.current?.children[jumpTo.index]?.scrollIntoView({ block: "start" });
    // The token is what makes pressing the same letter twice scroll twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpTo?.index, jumpTo?.token]);

  return (
    <div className={styles.grid} data-testid="album-grid" ref={grid}>
      {tiles.map(({ album: a, score }) => (
        <Link
          key={a.id}
          to="/album/$albumId"
          params={{ albumId: String(a.id) }}
          className={styles.tile}
          data-complete={score !== null ? "true" : "false"}
          data-testid="album-tile"
        >
          <div className={styles.tileBand} data-complete={score !== null ? "true" : "false"} />
          {a.art ? (
            <img className={styles.cover} src={a.art} alt="" />
          ) : (
            // The typographic fallback: the record's own title, set large, rather than a grey
            // box. A missing cover is still a record you recognise by name — and nothing ever
            // draws over real artwork, because this is what stands IN PLACE of it.
            <div className={styles.coverFallback}>{a.title}</div>
          )}
          <div className={styles.tileTitle}>{a.title}</div>
          <div className={styles.tileArtist}>{a.artist}</div>
          <div className={styles.tileMeta}>
            <span>{a.year ?? "—"}</span>
            {/* Never a number when incomplete (§6): a partial score is the defect, and a
                fraction on the tile is a provisional score wearing a different hat. */}
            <span data-incomplete={score === null ? "true" : undefined}>
              {score === null ? "INCOMPLETE" : score.toFixed(2)}
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
}
